import { API_BASE_URL } from "@/config/api";
import {
  PUBLIC_PET_REPORT_URL,
  PUBLIC_REPORTS_FEED_URL,
  REPORTS_API_PREFIX,
  reportCommentsUrl,
  reportFlagUrl,
  reportReactionsUrl,
} from "@/config/reports";
import {
  addLocalComment,
  addLocalReport,
  flagLocalReport,
  getLocalReport,
  listLocalReports,
  toggleLocalReaction,
} from "@/lib/localReportsStore";
import { fetchJson } from "@/services/free/http";

export function normalizeReport(report) {
  if (!report) return null;
  const id = report.id || report._id;
  if (!id) return null;

  const rawPhoto = report.photoUrl || report.photo || report.photoDataUrl;
  const photoUrl =
    typeof rawPhoto === "string" && rawPhoto.startsWith("http")
      ? rawPhoto
      : typeof rawPhoto === "string" && rawPhoto.startsWith("data:")
        ? rawPhoto
        : typeof rawPhoto === "string" && rawPhoto
          ? `${API_BASE_URL}${rawPhoto}`
          : null;

  const petTypeRaw = report.petType || report.pet_type || report.petKind || null;
  const petType = typeof petTypeRaw === "string" ? petTypeRaw.toLowerCase() : null;

  return {
    id,
    petName: report.petName || report.pet_name || report.pet || "",
    petType: petType === "cat" || petType === "dog" || petType === "other" ? petType : "other",
    firstName: report.firstName || report.name || "Anonymous",
    lastName: report.lastName || report.last_name || "",
    phoneNumber: report.phoneNumber || report.phone_number || "",
    postedBy: report.postedBy || report.posted_by || null,
    petStatus: (report.petStatus || report.status || "lost").toLowerCase(),
    location: report.location || "",
    createdAt:
      report.createdAt || report.created_at || report.timestamp || new Date().toISOString(),
    photoUrl,
    description: report.description || "",
    reactions: report.reactions || { like: 0, heart: 0, help: 0, seen: 0, helped: 0 },
    myReaction: report.myReaction || null,
    comments: Array.isArray(report.comments) ? report.comments : [],
    commentsCount: Number.isFinite(report.commentsCount)
      ? report.commentsCount
      : Array.isArray(report.comments)
        ? report.comments.length
        : 0,
  };
}

export async function createPublicReportFallbackToLocal(form) {
  return addLocalReport(form);
}

export async function submitPublicPetReport({
  petName,
  petType,
  firstName,
  lastName,
  phoneNumber,
  petStatus,
  location,
  description,
  photoFile,
}) {
  const formData = new FormData();
  if (petName) formData.append("petName", petName);
  if (petType) formData.append("petType", petType);
  formData.append("firstName", firstName);
  formData.append("lastName", lastName);
  formData.append("phoneNumber", phoneNumber);
  formData.append("petStatus", petStatus);
  formData.append("location", location);
  if (description) formData.append("description", description);
  if (photoFile) formData.append("photo", photoFile);

  try {
    const { res, data } = await fetchJson(PUBLIC_PET_REPORT_URL, {
      method: "POST",
      body: formData,
    });

    if (!res.ok) {
      const localReport = await createPublicReportFallbackToLocal({
        petName,
        petType,
        firstName,
        lastName,
        phoneNumber,
        petStatus,
        location,
        description,
        photoFile,
      }).catch(() => null);
      return {
        ok: true,
        data: { local: true },
        report: localReport,
        warning: data?.message || "Saved locally (server rejected request).",
      };
    }

    return { ok: true, data };
  } catch (error) {
    const localReport = await createPublicReportFallbackToLocal({
      petName,
      petType,
      firstName,
      lastName,
      phoneNumber,
      petStatus,
      location,
      description,
      photoFile,
    }).catch(() => null);
    return {
      ok: true,
      data: { local: true },
      report: localReport,
      warning: error?.message || "Saved locally (network error).",
    };
  }
}

export async function fetchPublicReports({ page = 1, limit = 10 } = {}) {
  const url = new URL(PUBLIC_REPORTS_FEED_URL);
  url.searchParams.set("page", String(page));
  url.searchParams.set("limit", String(limit));

  try {
    const res = await fetch(url.toString(), { method: "GET" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    const data = await res.json().catch(() => ({}));
    const items = (data?.items || data?.reports || data || [])
      .map(normalizeReport)
      .filter(Boolean);

    const nextPage = data?.nextPage ?? (items.length === limit ? page + 1 : null);
    return { ok: true, items, nextPage };
  } catch {
    return listLocalReports({ page, limit });
  }
}

export async function fetchPublicReport({ reportId }) {
  if (!reportId) return { ok: false, error: "Missing reportId" };
  const id = String(reportId);

  async function findInPublicFeed() {
    const limit = 20;
    let page = 1;
    let nextPage = 1;

    // Keep this bounded so opening a share link can't accidentally DDoS the API.
    const maxPages = 8;

    while (nextPage && page <= maxPages) {
      // eslint-disable-next-line no-await-in-loop
      const pageRes = await fetchPublicReports({ page, limit });
      if (!pageRes?.ok) return null;
      const match = (pageRes.items || []).find((r) => String(r?.id) === id);
      if (match) return match;
      nextPage = pageRes.nextPage;
      page = nextPage || page + 1;
    }

    return null;
  }

  try {
    const { res, data } = await fetchJson(`${REPORTS_API_PREFIX}/${encodeURIComponent(id)}`, {
      method: "GET",
    });
    if (!res.ok) {
      const found = await findInPublicFeed();
      if (found) return { ok: true, report: found, fromFeed: true };
      return { ok: false, error: data?.message || `HTTP ${res.status}` };
    }

    const report = normalizeReport(data?.report ?? data);
    if (!report) return { ok: false, error: "Report not found" };
    return { ok: true, report, data };
  } catch {
    const found = await findInPublicFeed();
    if (found) return { ok: true, report: found, fromFeed: true };

    const local = getLocalReport(id);
    const report = normalizeReport(local);
    if (!report) return { ok: false, error: "Report not found" };
    return { ok: true, report, local: true };
  }
}

export async function postComment({ reportId, name, text }) {
  try {
    const { res, data } = await fetchJson(reportCommentsUrl(reportId), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name, text }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const report = normalizeReport(data?.report);
    const comment = data?.comment || null;
    return { ok: true, data, report, comment };
  } catch {
    return addLocalComment(reportId, { name, text });
  }
}

export async function fetchComments({ reportId, page = 1, limit = 20 } = {}) {
  try {
    const url = new URL(reportCommentsUrl(reportId));
    url.searchParams.set("page", String(page));
    url.searchParams.set("limit", String(limit));

    const res = await fetch(url.toString(), { method: "GET" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json().catch(() => ({}));
    const items = Array.isArray(data?.items) ? data.items : [];
    const nextPage = data?.nextPage ?? null;
    return { ok: true, items, nextPage };
  } catch {
    const local = getLocalReport(reportId);
    const items = Array.isArray(local?.comments) ? local.comments : [];
    return { ok: true, items, nextPage: null };
  }
}

export async function toggleReaction({ reportId, reactionType }) {
  try {
    const { res, data } = await fetchJson(reportReactionsUrl(reportId), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reaction: reactionType }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const report = normalizeReport(data?.report);
    return { ok: true, data, report };
  } catch {
    return toggleLocalReaction(reportId, reactionType);
  }
}

export async function flagReport({ reportId, reason, details }) {
  try {
    const { res, data } = await fetchJson(reportFlagUrl(reportId), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reason, details }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return { ok: true, data };
  } catch {
    return flagLocalReport(reportId, { reason, details });
  }
}

export const reportsService = {
  normalizeReport,
  submitPublicPetReport,
  fetchPublicReports,
  fetchPublicReport,
  createPublicReportFallbackToLocal,
  postComment,
  fetchComments,
  toggleReaction,
  flagReport,
};
